import type { InitOptions } from 'i18next';
import type { Theme, Components } from '@mui/material/styles';

import resourcesToBackend from 'i18next-resources-to-backend';

// MUI Core Locales
import {
  frFR as frFRCore,
  viVN as viVNCore,
  zhCN as zhCNCore,
  arSA as arSACore,
} from '@mui/material/locale';
// MUI Date Pickers Locales
import {
  enUS as enUSDate,
  frFR as frFRDate,
  viVN as viVNDate,
  zhCN as zhCNDate,
} from '@mui/x-date-pickers/locales';
// MUI Data Grid Locales
import {
  enUS as enUSDataGrid,
  frFR as frFRDataGrid,
  viVN as viVNDataGrid,
  zhCN as zhCNDataGrid,
  arSD as arSDDataGrid,
} from '@mui/x-data-grid/locales';

// ----------------------------------------------------------------------

// Supported languages
export const supportedLngs = [
  'en',
  'de',
  'fr',
  'es',
  'tr',
  'it',
  'af',
  'am',
  'ar',
  'bg',
  'bs',
  'ca',
  'cs',
  'da',
  'nl',
  'et',
  'eu',
  'fi',
  'tl',
  'el',
  'km',
  'pl',
  'pt',
  'nn',
  'ru',
  'sl',
  'sv',
  'ta',
  'vi',
  'zh',
  'cn',
  'ja',
  'ko',
  'nb',
] as const;
export type LangCode = (typeof supportedLngs)[number];

// Fallback and default namespace
export const fallbackLng: LangCode = 'en';
export const defaultNS = 'translation';

// Storage config
export const storageConfig = {
  cookie: { key: 'i18next', autoDetection: false },
  localStorage: { key: 'i18nextLng', autoDetection: false },
} as const;

// ----------------------------------------------------------------------

/**
 * @countryCode https://flagcdn.com/en/codes.json
 * @adapterLocale https://github.com/iamkun/dayjs/tree/master/src/locale
 * @numberFormat https://simplelocalize.io/data/locales/
 */

export type LangOption = {
  value: LangCode;
  label: string;
  countryCode: string;
  adapterLocale?: string;
  numberFormat: { code: string; currency: string };
  systemValue?: { components: Components<Theme> };
};

export const allLangs: LangOption[] = [
  {
    value: 'en',
    label: 'English',
    countryCode: 'GB',
    adapterLocale: 'en',
    numberFormat: { code: 'en-US', currency: 'USD' },
    systemValue: {
      components: { ...enUSDate.components, ...enUSDataGrid.components },
    },
  },
  {
    value: 'de',
    label: 'Deutsch',
    countryCode: 'DE',
    adapterLocale: 'de',
    numberFormat: { code: 'de-DE', currency: 'EUR' },
  },
  {
    value: 'fr',
    label: 'Français',
    countryCode: 'FR',
    adapterLocale: 'fr',
    numberFormat: { code: 'fr-FR', currency: 'EUR' },
    systemValue: {
      components: {
        ...frFRCore.components,
        ...frFRDate.components,
        ...frFRDataGrid.components,
      },
    },
  },
  {
    value: 'es',
    label: 'Español',
    countryCode: 'ES',
    adapterLocale: 'es',
    numberFormat: { code: 'es-ES', currency: 'EUR' },
  },
  {
    value: 'tr',
    label: 'Türkçe',
    countryCode: 'TR',
    adapterLocale: 'tr',
    numberFormat: { code: 'tr-TR', currency: 'TRY' },
  },
  {
    value: 'it',
    label: 'Italiano',
    countryCode: 'IT',
    adapterLocale: 'it',
    numberFormat: { code: 'it-IT', currency: 'EUR' },
  },
  {
    value: 'af',
    label: 'Afrikaans',
    countryCode: 'ZA',
    adapterLocale: 'af',
    numberFormat: { code: 'af-ZA', currency: 'ZAR' },
  },
  {
    value: 'am',
    label: 'አማርኛ',
    countryCode: 'ET',
    adapterLocale: 'am',
    numberFormat: { code: 'am-ET', currency: 'ETB' },
  },
  {
    value: 'ar',
    label: 'العربية',
    countryCode: 'SA',
    adapterLocale: 'ar-sa',
    numberFormat: { code: 'ar-SA', currency: 'SAR' },
    systemValue: {
      components: { ...arSACore.components, ...arSDDataGrid.components },
    },
  },
  {
    value: 'bg',
    label: 'Български език',
    countryCode: 'BG',
    adapterLocale: 'bg',
    numberFormat: { code: 'bg-BG', currency: 'BGN' },
  },
  {
    value: 'bs',
    label: 'Bosanski',
    countryCode: 'BA',
    adapterLocale: 'bs',
    numberFormat: { code: 'bs-BA', currency: 'BAM' },
  },
  {
    value: 'ca',
    label: 'Català',
    countryCode: 'ES',
    adapterLocale: 'ca',
    numberFormat: { code: 'ca-ES', currency: 'EUR' },
  },
  {
    value: 'cs',
    label: 'Čeština',
    countryCode: 'CZ',
    adapterLocale: 'cs',
    numberFormat: { code: 'cs-CZ', currency: 'CZK' },
  },
  {
    value: 'da',
    label: 'Dansk',
    countryCode: 'DK',
    adapterLocale: 'da',
    numberFormat: { code: 'da-DK', currency: 'DKK' },
  },
  {
    value: 'nl',
    label: 'Nederlands',
    countryCode: 'NL',
    adapterLocale: 'nl',
    numberFormat: { code: 'nl-NL', currency: 'EUR' },
  },
  {
    value: 'et',
    label: 'Eesti',
    countryCode: 'EE',
    adapterLocale: 'et',
    numberFormat: { code: 'et-EE', currency: 'EUR' },
  },
  {
    value: 'eu',
    label: 'Euskara',
    countryCode: 'ES',
    adapterLocale: 'eu',
    numberFormat: { code: 'eu-ES', currency: 'EUR' },
  },
  {
    value: 'fi',
    label: 'Suomi',
    countryCode: 'FI',
    adapterLocale: 'fi',
    numberFormat: { code: 'fi-FI', currency: 'EUR' },
  },
  {
    value: 'tl',
    label: 'Filipino',
    countryCode: 'PH',
    adapterLocale: 'tl',
    numberFormat: { code: 'tl-PH', currency: 'PHP' },
  },
  {
    value: 'el',
    label: 'Ελληνικά',
    countryCode: 'GR',
    adapterLocale: 'el',
    numberFormat: { code: 'el-GR', currency: 'EUR' },
  },
  {
    value: 'km',
    label: 'ខ្មែរ',
    countryCode: 'KH',
    adapterLocale: 'km',
    numberFormat: { code: 'km-KH', currency: 'KHR' },
  },
  {
    value: 'pl',
    label: 'Polski',
    countryCode: 'PL',
    adapterLocale: 'pl',
    numberFormat: { code: 'pl-PL', currency: 'PLN' },
  },
  {
    value: 'pt',
    label: 'Português',
    countryCode: 'PT',
    adapterLocale: 'pt',
    numberFormat: { code: 'pt-PT', currency: 'EUR' },
  },
  {
    value: 'nn',
    label: 'Norsk Nynorsk',
    countryCode: 'NO',
    adapterLocale: 'nn',
    numberFormat: { code: 'nn-NO', currency: 'NOK' },
  },
  {
    value: 'ru',
    label: 'Русский',
    countryCode: 'RU',
    adapterLocale: 'ru',
    numberFormat: { code: 'ru-RU', currency: 'RUB' },
  },
  {
    value: 'sl',
    label: 'Slovenščina',
    countryCode: 'SI',
    adapterLocale: 'sl',
    numberFormat: { code: 'sl-SI', currency: 'EUR' },
  },
  {
    value: 'sv',
    label: 'Svenska',
    countryCode: 'SE',
    adapterLocale: 'sv',
    numberFormat: { code: 'sv-SE', currency: 'SEK' },
  },
  {
    value: 'ta',
    label: 'தமிழ்',
    countryCode: 'LK',
    adapterLocale: 'ta',
    numberFormat: { code: 'ta-LK', currency: 'LKR' },
  },
  {
    value: 'vi',
    label: 'Tiếng Việt',
    countryCode: 'VN',
    adapterLocale: 'vi',
    numberFormat: { code: 'vi-VN', currency: 'VND' },
    systemValue: {
      components: {
        ...viVNCore.components,
        ...viVNDate.components,
        ...viVNDataGrid.components,
      },
    },
  },
  {
    value: 'zh',
    label: '中文',
    countryCode: 'CN',
    adapterLocale: 'zh-cn',
    numberFormat: { code: 'zh-CN', currency: 'CNY' },
    systemValue: {
      components: {
        ...zhCNCore.components,
        ...zhCNDate.components,
        ...zhCNDataGrid.components,
      },
    },
  },
  {
    value: 'cn',
    label: '简体中文',
    countryCode: 'CN',
    adapterLocale: 'zh-cn',
    numberFormat: { code: 'zh-CN', currency: 'CNY' },
    systemValue: {
      components: {
        ...zhCNCore.components,
        ...zhCNDate.components,
        ...zhCNDataGrid.components,
      },
    },
  },
  {
    value: 'ja',
    label: '日本語',
    countryCode: 'JP',
    adapterLocale: 'ja',
    numberFormat: { code: 'ja-JP', currency: 'JPY' },
  },
  {
    value: 'ko',
    label: '한국어',
    countryCode: 'KR',
    adapterLocale: 'ko',
    numberFormat: { code: 'ko-KR', currency: 'KRW' },
  },
  {
    value: 'nb',
    label: 'Norsk Bokmål',
    countryCode: 'NO',
    adapterLocale: 'nb',
    numberFormat: { code: 'nb-NO', currency: 'NOK' },
  },
];

// ----------------------------------------------------------------------

export const i18nResourceLoader = resourcesToBackend(
  (lang: LangCode) => import(`./langs/${lang}.json`)
);

export function i18nOptions(lang = fallbackLng): InitOptions {
  return {
    // debug: true,
    supportedLngs,
    fallbackLng,
    lng: lang,
    /********/
    fallbackNS: defaultNS,
    defaultNS,
    ns: defaultNS,
  };
}

export function getCurrentLang(lang?: string): LangOption {
  const fallbackLang = allLangs.find((l) => l.value === fallbackLng) ?? allLangs[0];

  if (!lang) {
    return fallbackLang;
  }

  return allLangs.find((l) => l.value === lang) ?? fallbackLang;
}
